/**
 * Reload the page — but never out from under a live call.
 *
 * WHY: several places reload automatically (trial expiry timer, permission changes,
 * stale-chunk recovery). When one of those fires mid-dial it does two bad things:
 * it kills the in-memory Twilio device and the active call, and — because
 * CallSafeReloadGuard registers a `beforeunload` blocker during calls — the agent
 * gets a native "Leave site?" dialog they never asked for, mid-conversation with a
 * lead. That's the "makes you reload the page" bug report.
 *
 * `safeReload()` reloads immediately when it's safe, and otherwise WAITS for the call
 * to end and reloads then. The page still ends up on the new build; the agent just
 * isn't interrupted to get there.
 *
 * `__ic_onCall` is the global flag CallSafeReloadGuard keeps in sync — it covers live
 * WebRTC calls, power/multi-line dialer sessions, ringing/held/coaching states, and
 * an explicit 'on_call' presence.
 */

const POLL_MS = 2000;
// Stop waiting after 2h. A flag stuck 'true' (a crashed provider, a session never
// cleaned up) must not mean the tab can never pick up a new build again.
const MAX_WAIT_MS = 2 * 60 * 60 * 1000;

let pendingTimer: ReturnType<typeof setInterval> | null = null;

export function isOnCall(): boolean {
  try {
    return !!(window as any).__ic_onCall;
  } catch {
    return false;
  }
}

/**
 * @param reason short label for logs — e.g. 'trial-expired', 'stale-chunk'
 */
export function safeReload(reason = 'unspecified'): void {
  if (typeof window === 'undefined') return;

  if (!isOnCall()) {
    window.location.reload();
    return;
  }

  // Already waiting from an earlier call — don't stack timers.
  if (pendingTimer) return;

  const startedAt = Date.now();
  // eslint-disable-next-line no-console
  console.info(`[safeReload] deferred (${reason}) — call in progress, will reload when it ends`);

  pendingTimer = setInterval(() => {
    const waited = Date.now() - startedAt;

    if (!isOnCall()) {
      clearInterval(pendingTimer!);
      pendingTimer = null;
      window.location.reload();
      return;
    }

    if (waited > MAX_WAIT_MS) {
      // Give up waiting rather than pin this tab to an old build forever. Don't force
      // a reload either — VersionCheck's "New Version Available" toast is still there
      // for the user to take when they're ready.
      clearInterval(pendingTimer!);
      pendingTimer = null;
      // eslint-disable-next-line no-console
      console.warn(`[safeReload] gave up waiting (${reason}) after 2h — leaving the page alone`);
    }
  }, POLL_MS);
}

/** True if a reload is queued behind an active call. */
export function isReloadPending(): boolean {
  return pendingTimer !== null;
}
